import { useEffect, useMemo, useState } from "react";
import api from '../../services/apiClient.ts';
import { toast } from "react-toastify";
import Spinner from "../common/Spinner";
import Modal from "../common/Modal";
import { useTranslation } from "react-i18next";
import { Plus, Search, Pencil, Trash2 } from "lucide-react";

export default function AgentsPage() {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [agents, setAgents] = useState([]);
  const [q, setQ] = useState("");

  const [openAdd, setOpenAdd] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [selected, setSelected] = useState(null);

  const [form, setForm] = useState({
    name: "",
    branch_id: "",
    opening_balance: "",
  });

  const resetForm = () => {
    setForm({
        name: "",
        branch_id: "",
        opening_balance: "",
    });
  };

  const headersAuth = {
    Authorization: `Bearer ${localStorage.getItem("token")}`,
    "X-Tenant": localStorage.getItem("tenant_key"),
    "X-Branch": localStorage.getItem("branch_id"),
  };

  const loadData = async () => {
    try {
      const resp = await api.get("/v1/agents", { headers: headersAuth });
      setAgents(resp.data.data);
    } catch (err) {
      console.log(err);
      toast.error(t("agents.loadFailed"));
    }
    setLoading(false);
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleAdd = async () => {
    if (!form.name.trim()) return toast.error(t("agents.nameRequired"));

    try {
      await api.post(
        "/v1/agents",
        {
          name: form.name,
          branch_id: Number(form.branch_id) || null,
          opening_balance: Number(form.opening_balance) || 0,
        },
        { headers: headersAuth }
      );

      toast.success(t("agents.added"));
      resetForm();
      setOpenAdd(false);
      loadData();
    } catch (err) {
      console.log(err);
      toast.error(t("agents.saveFailed"));
    }
  };

  const handleEdit = async () => {
    if (!selected) return;

    try {
      await api.put(
        `/v1/agents/${selected.id}`,
        {
          name: form.name,
          branch_id: Number(form.branch_id) || null,
          opening_balance: Number(form.opening_balance) || 0,
        },
        { headers: headersAuth }
      );

      toast.success(t("agents.updated"));
      resetForm();
      setOpenEdit(false);
      loadData();
    } catch (err) {
      console.log(err);
      toast.error(t("agents.saveFailed"));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t("agents.confirmDelete"))) return;

    try {
      await api.delete(`/v1/agents/${id}`, { headers: headersAuth });
      toast.success(t("agents.deleted"));
      loadData();
    } catch (err) {
      console.log(err);
      toast.error(t("agents.deleteFailed"));
    }
  };

  const filteredAgents = useMemo(() => {
    const query = q.trim().toLowerCase();
    if (!query) return agents;
    return agents.filter((a) => {
      const name = String(a.name || "").toLowerCase();
      const branch = String(a.branch_id ?? "").toLowerCase();
      const opening = String(a.opening_balance ?? "").toLowerCase();
      return (
        name.includes(query) || branch.includes(query) || opening.includes(query)
      );
    });
  }, [agents, q]);

  if (loading) return <Spinner />;

  return (
    <div className="main-content p-6">
      <div className="w-full mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="page-title text-2xl font-semibold text-gray-900 dark:text-gray-100">
              {t("agents.title")}
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {t("agents.subtitle")}
            </p>
          </div>
          <button
            className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-primary-600 text-white hover:bg-primary-700"
            onClick={() => setOpenAdd(true)}
          >
            <Plus className="w-4 h-4" />
            {t("agents.addAgent")}
          </button>
        </div>

        <div className="search-container mb-4">
          <div className="relative w-full md:w-96">
            <Search className="absolute top-1/2 -translate-y-1/2 ltr:left-3 rtl:right-3 w-4 h-4 text-gray-400" />
            <input
              className="w-full h-10 rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 pl-10 rtl:pl-3 rtl:pr-10 focus:outline-none focus:ring-2 focus:ring-primary-500"
              placeholder={t("agents.searchPlaceholder")}
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>
        </div>

        <div className="app-table-wrap shadow-sm">
          <table className="app-table min-w-full">
            <thead className="bg-gray-50 dark:bg-gray-800">
              <tr>
                <th className="px-4 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 text-right rtl:text-right">#</th>
                <th className="px-4 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 text-right rtl:text-right">{t("agents.name")}</th>
                <th className="px-4 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 text-right rtl:text-right">{t("agents.branch")}</th>
                <th className="px-4 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 text-right rtl:text-right">{t("agents.openingBalance")}</th>
                <th className="px-4 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 text-right rtl:text-right">{t("agents.actions")}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-800">
              {filteredAgents.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-4 py-10 text-center text-gray-500 dark:text-gray-400">{t("agents.noData")}</td>
                </tr>
              )}
              {filteredAgents.length > 0 && filteredAgents.map((a, i) => (
                <tr key={a.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                  <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-200 text-right rtl:text-right">{i + 1}</td>
                  <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100 text-right rtl:text-right">{a.name}</td>
                  <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100 text-right rtl:text-right">{a.branch_id ?? "-"}</td>
                  <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100 text-right rtl:text-right">{Number(a.opening_balance).toLocaleString()}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3   justify-end text-right rtl:justify-start">
                      <button
                        className="inline-flex items-center gap-1 text-primary-600 hover:text-primary-700"
                        onClick={() => {
                          setSelected(a);
                          setForm({
                            name: a.name,
                            branch_id: a.branch_id,
                            opening_balance: a.opening_balance,
                          });
                          setOpenEdit(true);
                        }}
                      >
                        <Pencil className="w-4 h-4" />
                        <span className="text-sm">{t("agents.editAgent")}</span>
                      </button>
                      <button
                        className="inline-flex items-center gap-1 text-red-600 hover:text-red-700"
                        onClick={() => handleDelete(a.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                        <span className="text-sm">{t("common.delete")}</span>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

      {/* ADD MODAL */}
      <Modal show={openAdd} onClose={() => setOpenAdd(false)} title={t("agents.addAgent")} showClose={false}>
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">{t("agents.name")}</label>
            <input
              className="w-full h-10 rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 px-3 focus:outline-none focus:ring-2 focus:ring-primary-500"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">{t("agents.openingBalance")}</label>
            <input
              className="w-full h-10 rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 px-3 focus:outline-none focus:ring-2 focus:ring-primary-500"
              type="number"
              value={form.opening_balance}
              onChange={(e) => setForm({ ...form, opening_balance: e.target.value })}
            />
          </div>
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <button className="px-4 py-2 rounded-md border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800" onClick={() => setOpenAdd(false)}>
            {t("common.cancel")}
          </button>
          <button className="px-4 py-2 rounded-md bg-primary-600 text-white hover:bg-primary-700" onClick={handleAdd}>
            {t("common.save")}
          </button>
        </div>
      </Modal>

      {/* EDIT MODAL */}
      <Modal show={openEdit} onClose={() => setOpenEdit(false)} title={t("agents.editAgent")}>
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">{t("agents.name")}</label>
            <input
              className="w-full h-10 rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 px-3 focus:outline-none focus:ring-2 focus:ring-primary-500"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">{t("agents.branch")}</label>
            <input
              className="w-full h-10 rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 px-3 focus:outline-none focus:ring-2 focus:ring-primary-500"
              type="number"
              value={form.branch_id}
              onChange={(e) => setForm({ ...form, branch_id: e.target.value })}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">{t("agents.openingBalance")}</label>
            <input
              className="w-full h-10 rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 px-3 focus:outline-none focus:ring-2 focus:ring-primary-500"
              type="number"
              value={form.opening_balance}
              onChange={(e) => setForm({ ...form, opening_balance: e.target.value })}
            />
          </div>
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <button className="px-4 py-2 rounded-md border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800" onClick={() => setOpenEdit(false)}>
            {t("common.cancel")}
          </button>
          <button className="px-4 py-2 rounded-md bg-primary-600 text-white hover:bg-primary-700" onClick={handleEdit}>
            {t("common.save")}
          </button>
        </div>
      </Modal>
      </div>
    </div>
  );
}
